import { useCallback, useSyncExternalStore } from 'react';

/**
 * Whether the desktop sidebar is collapsed to an icon rail.
 *
 * Kept in a tiny module-level store rather than component state because two
 * separate pieces of chrome need the same value: the sidebar (which renders the
 * rail) and DashboardLayout (which owns the left padding the rail sits in). It
 * is persisted to localStorage so the choice survives navigation and reload,
 * which is the whole point of the preference.
 */
export const SIDEBAR_COLLAPSED_STORAGE_KEY = 'policy-sidebar-collapsed';

type Listener = () => void;

function safeStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    // Safari private mode / storage disabled by policy: the preference simply
    // does not persist. It must never throw inside chrome that every page renders.
    return null;
  }
}

/**
 * Read the persisted preference. Anything other than the literal `'true'`
 * (missing key, corrupt value, storage unavailable) means "expanded", so a bad
 * localStorage entry can never hide the navigation.
 */
export function readStoredCollapsed(storage: Storage | null = safeStorage()): boolean {
  if (!storage) return false;
  try {
    return storage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function writeStoredCollapsed(
  value: boolean,
  storage: Storage | null = safeStorage()
): void {
  if (!storage) return;
  try {
    storage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, value ? 'true' : 'false');
  } catch {
    // Quota / disabled storage. The in-memory value still drives this tab.
  }
}

const listeners = new Set<Listener>();
let collapsedState = readStoredCollapsed();

function emit() {
  listeners.forEach((listener) => listener());
}

export function getSidebarCollapsed(): boolean {
  return collapsedState;
}

export function setSidebarCollapsed(value: boolean): void {
  if (collapsedState === value) return;
  collapsedState = value;
  writeStoredCollapsed(value);
  emit();
}

export function toggleSidebarCollapsed(): void {
  setSidebarCollapsed(!collapsedState);
}

/**
 * Re-read localStorage into the store. Wired to the `storage` event so a second
 * tab collapsing the sidebar is reflected here; also the seam tests use to load
 * a preference that was written after this module was imported.
 */
export function syncSidebarCollapsedFromStorage(): void {
  const next = readStoredCollapsed();
  if (next === collapsedState) return;
  collapsedState = next;
  emit();
}

function subscribe(listener: Listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('storage', (event: StorageEvent) => {
    if (event.key !== null && event.key !== SIDEBAR_COLLAPSED_STORAGE_KEY) return;
    syncSidebarCollapsedFromStorage();
  });
}

export interface SidebarCollapsedApi {
  collapsed: boolean;
  setCollapsed: (value: boolean) => void;
  toggleCollapsed: () => void;
}

export function useSidebarCollapsed(): SidebarCollapsedApi {
  const collapsed = useSyncExternalStore(subscribe, getSidebarCollapsed, () => false);
  const setCollapsed = useCallback((value: boolean) => setSidebarCollapsed(value), []);
  const toggleCollapsed = useCallback(() => toggleSidebarCollapsed(), []);
  return { collapsed, setCollapsed, toggleCollapsed };
}
